/**
 * AlertSummaryView — Summary statistics for the Alert Details Report.
 *
 * Displays:
 *   1. Stat cards row (Total Events, Avg Response, Avg Service)
 *   2. Event Reason Breakdown table
 *   3. Two-column grid: Most Active Residents + Most Active Caregivers
 *
 * Props:
 *   summaryData    — { count, avg_response_time, avg_service_time, reasons, mostActiveResident, mostActiveCaregiver }
 *   startDisplay   — formatted start datetime string
 *   endDisplay     — formatted end datetime string
 */

import { Link } from 'react-router-dom';

const AlertSummaryView = ({ summaryData, startDisplay, endDisplay }) => {
    if (!summaryData || typeof summaryData !== 'object') {
        return (
            <div className="ar-summary-empty">
                <p className="dp-cell-text dp-cell-text--muted">No summary data available.</p>
            </div>
        );
    }

    const reasons = summaryData.reasons || [];
    const activeResidents = summaryData.mostActiveResident || [];
    const activeCaregivers = summaryData.mostActiveCaregiver || [];

    /* `== null` ALONE -- the zero arms are gone, and dropping them was the point.

       Every source this formats now returns NULL for "nothing measurable"
       (docs/REPORT_PROC_AUDIT_PLAN.md phases 3-4):

         summaryData.avg_service_time  <- the DETAIL rows (Option A, D11)
         reason rows                   <- sp_getReasonCodeStat / ...Cloud
         resident rows                 <- sp_getResidentStat / ...Cloud
         caregiver rows                <- sp_getCaregiverStat / ...Cloud

       Keeping `=== 0 || === '0' || === '0.0'` would now be a BUG rather than a
       safety net: a service time genuinely measured at under three seconds
       rounds to 0.0, and those arms would print it as "no measurement". The
       three variants existed because the Lambda emitted a string in one branch
       and a bare float in another; it emits one type per field now. */
    const formatServiceTime = (val) => {
        if (val == null || val === '') return '-';
        return val;
    };

    return (
        <div className="ar-summary">
            {/* Date range label */}
            <p className="ar-summary__range">
                {startDisplay} &mdash; {endDisplay}
            </p>

            {/* Stat cards row */}
            <div className="ar-summary-stats">
                <div className="ar-summary-card">
                    <div className="ar-summary-card__value">
                        {summaryData.count != null ? summaryData.count.toLocaleString() : '0'}
                    </div>
                    <div className="ar-summary-card__label">Total Events</div>
                </div>
                <div className="ar-summary-card">
                    <div className="ar-summary-card__value">
                        {summaryData.avg_response_time != null ? summaryData.avg_response_time : '-'}
                    </div>
                    <div className="ar-summary-card__label">Avg Response (min)</div>
                </div>
                <div className="ar-summary-card">
                    <div className="ar-summary-card__value">
                        {formatServiceTime(summaryData.avg_service_time)}
                    </div>
                    <div className="ar-summary-card__label">Avg Service (min)</div>
                </div>
            </div>

            {/* Event Reason Breakdown */}
            <div className="pp-section" style={{ marginTop: 'var(--space-6)' }}>
                <h4 className="pp-section__title">Event Reason Breakdown</h4>
                <div className="dp-table-wrap">
                    <table className="dp-table">
                        <thead className="dp-thead">
                            <tr>
                                <th className="dp-th">Reason</th>
                                <th className="dp-th dp-th--center">Total</th>
                                <th className="dp-th">% Volume</th>
                                <th className="dp-th dp-th--center">Avg Response (min)</th>
                                <th className="dp-th dp-th--center">Avg Service (min)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reasons.length > 0 ? reasons.map((r, idx) => (
                                <tr key={idx} className="dp-tr">
                                    <td className="dp-td">
                                        <span className="dp-cell-text">{r.reason_name || 'Unknown'}</span>
                                    </td>
                                    <td className="dp-td dp-td--center">
                                        <span className="dp-cell-text">{r.count != null ? r.count.toLocaleString() : '0'}</span>
                                    </td>
                                    <td className="dp-td">
                                        <div className="ip-percent-bar">
                                            <div className="ip-percent-bar__track">
                                                <div className="ip-percent-bar__fill" style={{ width: `${parseFloat(r.percent) || 0}%` }} />
                                            </div>
                                            <span className="ip-percent-bar__label">{r.percent || '0%'}</span>
                                        </div>
                                    </td>
                                    <td className="dp-td dp-td--center">
                                        <span className="dp-cell-text">{r.avg_response_time != null ? r.avg_response_time : '-'}</span>
                                    </td>
                                    <td className="dp-td dp-td--center">
                                        <span className="dp-cell-text">{formatServiceTime(r.avg_service_time)}</span>
                                    </td>
                                </tr>
                            )) : (
                                <tr className="dp-tr">
                                    <td className="dp-td dp-td--center" colSpan={5} style={{ padding: 'var(--space-6)' }}>
                                        <span className="dp-cell-text dp-cell-text--muted">No reason data for this period.</span>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Two-column grid: Most Active Residents + Caregivers */}
            <div className="ar-summary-grid">
                {/* Most Active Residents */}
                <div className="pp-section">
                    <h4 className="pp-section__title">Most Active Residents</h4>
                    <div className="dp-table-wrap">
                        <table className="dp-table">
                            <thead className="dp-thead">
                                <tr>
                                    <th className="dp-th">Resident</th>
                                    <th className="dp-th dp-th--center">Total</th>
                                    <th className="dp-th">% Volume</th>
                                    <th className="dp-th dp-th--center">Avg Response</th>
                                    <th className="dp-th dp-th--center">Avg Service</th>
                                </tr>
                            </thead>
                            <tbody>
                                {activeResidents.length > 0 ? activeResidents.map((r, idx) => (
                                    <tr key={idx} className="dp-tr">
                                        <td className="dp-td">
                                            {r.resident_id ? (
                                                <Link to={`/profiles/resident/${r.resident_id}`} className="dp-link">
                                                    {r.resident_name || 'Unknown'}
                                                </Link>
                                            ) : (
                                                <span className="dp-cell-text">{r.resident_name || 'Unknown'}</span>
                                            )}
                                        </td>
                                        <td className="dp-td dp-td--center">
                                            <span className="dp-cell-text">{r.count != null ? r.count.toLocaleString() : '0'}</span>
                                        </td>
                                        <td className="dp-td">
                                            <div className="ip-percent-bar">
                                                <div className="ip-percent-bar__track">
                                                    <div className="ip-percent-bar__fill" style={{ width: `${parseFloat(r.percent) || 0}%` }} />
                                                </div>
                                                <span className="ip-percent-bar__label">{r.percent || '0%'}</span>
                                            </div>
                                        </td>
                                        <td className="dp-td dp-td--center">
                                            <span className="dp-cell-text">{r.avg_response_time != null ? r.avg_response_time : '-'}</span>
                                        </td>
                                        <td className="dp-td dp-td--center">
                                            <span className="dp-cell-text">{formatServiceTime(r.avg_service_time)}</span>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr className="dp-tr">
                                        <td className="dp-td dp-td--center" colSpan={5} style={{ padding: 'var(--space-6)' }}>
                                            <span className="dp-cell-text dp-cell-text--muted">No resident data for this period.</span>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Most Active Caregivers */}
                <div className="pp-section">
                    <h4 className="pp-section__title">Most Active Caregivers</h4>
                    <div className="dp-table-wrap">
                        <table className="dp-table">
                            <thead className="dp-thead">
                                <tr>
                                    <th className="dp-th">Caregiver</th>
                                    <th className="dp-th dp-th--center">Total</th>
                                    <th className="dp-th">% Volume</th>
                                    <th className="dp-th dp-th--center">Avg Response</th>
                                    <th className="dp-th dp-th--center">Avg Service</th>
                                </tr>
                            </thead>
                            <tbody>
                                {activeCaregivers.length > 0 ? activeCaregivers.map((c, idx) => (
                                    <tr key={idx} className="dp-tr">
                                        <td className="dp-td">
                                            <span className="dp-cell-text">{c.display_name || 'Unknown'}</span>
                                        </td>
                                        <td className="dp-td dp-td--center">
                                            <span className="dp-cell-text">{c.count != null ? c.count.toLocaleString() : '0'}</span>
                                        </td>
                                        <td className="dp-td">
                                            <div className="ip-percent-bar">
                                                <div className="ip-percent-bar__track">
                                                    <div className="ip-percent-bar__fill" style={{ width: `${parseFloat(c.percent) || 0}%` }} />
                                                </div>
                                                <span className="ip-percent-bar__label">{c.percent || '0%'}</span>
                                            </div>
                                        </td>
                                        <td className="dp-td dp-td--center">
                                            <span className="dp-cell-text">{c.avg_response_time != null ? c.avg_response_time : '-'}</span>
                                        </td>
                                        <td className="dp-td dp-td--center">
                                            <span className="dp-cell-text">{formatServiceTime(c.avg_service_time)}</span>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr className="dp-tr">
                                        <td className="dp-td dp-td--center" colSpan={5} style={{ padding: 'var(--space-6)' }}>
                                            <span className="dp-cell-text dp-cell-text--muted">No caregiver data for this period.</span>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AlertSummaryView;
